(function () {
  'use strict';

  if (window.BGDesignGallery) return;

  function text(value) {
    return String(value || '').trim();
  }

  function escapeHtml(value) {
    return text(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function safeUrl(value) {
    var cleanValue = text(value);
    if (!cleanValue) return '';
    if (cleanValue.indexOf('/') === 0 && cleanValue.indexOf('//') !== 0) return cleanValue;
    if (/^https?:\/\//i.test(cleanValue)) return cleanValue;
    return '';
  }

  function toVotes(value, fallbackValue) {
    var parsed = Number(value);
    if (!Number.isFinite(parsed)) return Number.isFinite(fallbackValue) ? fallbackValue : 0;
    return Math.max(0, Math.round(parsed));
  }

  function normalizeItem(rawItem) {
    var item = rawItem && typeof rawItem === 'object' ? rawItem : {};
    return {
      slug: text(item.slug),
      title: text(item.title) || 'Untitled design',
      authorDisplayName: text(item.authorDisplayName),
      previewImageUrl: safeUrl(item.previewImageUrl),
      votes: toVotes(item.votes, 0),
      viewerVoted: item.viewerVoted === true,
      allowDuplicate: item.allowDuplicate === true,
      sharePath: safeUrl(item.sharePath)
    };
  }

  function initRoot(root) {
    if (!root) return null;

    var proxyRoot = text(root.getAttribute('data-proxy-root')) || '/apps/qps-commerce-control-plane/designer';
    var customerId = text(root.getAttribute('data-customer-id'));
    // Default matches the live WCR page handle (template my-designs-v3 is
    // assigned to the 'account-my-designs' page, not a same-named handle).
    var myDesignsUrl = safeUrl(root.getAttribute('data-my-designs-url')) || '/pages/account-my-designs';
    var statusNode = root.querySelector('[data-bg-gallery-status]');
    var listNode = root.querySelector('[data-bg-gallery-list]');
    var moreNode = root.querySelector('[data-bg-gallery-more]');
    var loadMoreButton = root.querySelector('[data-bg-gallery-load-more]');
    var state = {
      items: [],
      hasNextPage: false,
      nextCursor: '',
      loading: false,
      pending: {}
    };

    function buildLoginUrl() {
      var loginTarget = text(root.getAttribute('data-login-url')) || '/account/login';
      var loginUrl = new URL(loginTarget, window.location.origin);
      loginUrl.searchParams.set('return_url', window.location.pathname);
      try {
        var currentUrl = new URL(window.location.href, window.location.origin);
        var previewThemeId = text(currentUrl.searchParams.get('preview_theme_id'));
        if (previewThemeId) loginUrl.searchParams.set('preview_theme_id', previewThemeId);
      } catch (_) {}
      return loginUrl.toString();
    }

    function redirectToLogin() {
      window.location.assign(buildLoginUrl());
      return false;
    }

    function setStatus(message, tone) {
      if (!statusNode) return;
      statusNode.className = 'bg-design-gallery__status' + (tone ? (' is-' + tone) : '');
      statusNode.textContent = text(message);
    }

    function requestJson(url, options) {
      return fetch(url, Object.assign({
        credentials: 'same-origin',
        headers: {
          accept: 'application/json'
        }
      }, options || {})).then(function (response) {
        return response.json().catch(function () {
          return null;
        }).then(function (payload) {
          if (response.status === 401 || text(payload && payload.error).toLowerCase() === 'login required') {
            redirectToLogin();
            throw new Error('Login required.');
          }
          if (!response.ok || !payload || payload.ok !== true) {
            throw new Error(text(payload && payload.error) || 'Request failed.');
          }
          return payload;
        });
      });
    }

    function findItem(slug) {
      return state.items.find(function (entry) { return entry.slug === slug; });
    }

    function renderVoteButtonState(button, item) {
      button.classList.toggle('is-voted', item.viewerVoted);
      button.setAttribute('aria-pressed', item.viewerVoted ? 'true' : 'false');
      button.setAttribute('aria-label', (item.viewerVoted ? 'Remove your vote for ' : 'Vote for ') + item.title);
      var countNode = button.querySelector('[data-bg-gallery-vote-count]');
      if (countNode) countNode.textContent = String(item.votes);
    }

    function renderItems() {
      if (!listNode) return;
      if (state.loading && !state.items.length) {
        listNode.innerHTML = '<div class="bg-design-gallery__empty">Loading shared designs…</div>';
        return;
      }
      if (!state.items.length) {
        listNode.innerHTML = '<div class="bg-design-gallery__empty">No shared designs yet.</div>';
        return;
      }
      listNode.innerHTML = state.items.map(function (item) {
        var previewInner = item.previewImageUrl
          ? '<img src="' + escapeHtml(item.previewImageUrl) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">'
          : '<span class="bg-design-gallery__preview-fallback" aria-hidden="true"></span>';
        var preview = item.sharePath
          ? '<a class="bg-design-gallery__preview" href="' + escapeHtml(item.sharePath) + '" aria-label="' + escapeHtml('View ' + item.title) + '">' + previewInner + '</a>'
          : '<div class="bg-design-gallery__preview">' + previewInner + '</div>';
        var title = item.sharePath
          ? '<a href="' + escapeHtml(item.sharePath) + '">' + escapeHtml(item.title) + '</a>'
          : escapeHtml(item.title);
        return '' +
          '<article class="bg-design-gallery__card">' +
            preview +
            '<div class="bg-design-gallery__body">' +
              '<h2 class="bg-design-gallery__title">' + title + '</h2>' +
              (item.authorDisplayName ? '<p class="bg-design-gallery__credit">By ' + escapeHtml(item.authorDisplayName) + '</p>' : '') +
              '<div class="bg-design-gallery__actions">' +
                '<button type="button" class="bg-design-gallery__vote' + (item.viewerVoted ? ' is-voted' : '') + '"' +
                  ' data-bg-gallery-vote="' + escapeHtml(item.slug) + '"' +
                  ' aria-pressed="' + (item.viewerVoted ? 'true' : 'false') + '"' +
                  ' aria-label="' + escapeHtml((item.viewerVoted ? 'Remove your vote for ' : 'Vote for ') + item.title) + '">' +
                  '<span aria-hidden="true">♥</span>' +
                  '<span data-bg-gallery-vote-count>' + escapeHtml(String(item.votes)) + '</span>' +
                '</button>' +
                (item.allowDuplicate
                  ? '<button type="button" class="button button--secondary" data-bg-gallery-duplicate="' + escapeHtml(item.slug) + '">Use this design</button>'
                  : '') +
              '</div>' +
            '</div>' +
          '</article>';
      }).join('');
    }

    function renderLoadMore() {
      if (!moreNode) return;
      moreNode.hidden = !state.hasNextPage;
      if (loadMoreButton) loadMoreButton.disabled = state.loading;
    }

    function voteItem(slug, button) {
      var item = findItem(slug);
      if (!item) return Promise.resolve(false);
      if (!customerId) return Promise.resolve(redirectToLogin());
      if (state.pending[slug]) return Promise.resolve(false);
      state.pending[slug] = true;
      button.disabled = true;
      setStatus('', '');
      return requestJson(proxyRoot + '/gallery/vote', {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json'
        },
        body: JSON.stringify({ slug: slug, action: item.viewerVoted ? 'unvote' : 'vote' })
      }).then(function (payload) {
        var vote = payload && payload.vote && typeof payload.vote === 'object' ? payload.vote : {};
        item.viewerVoted = vote.voted === true;
        item.votes = toVotes(vote.votes, item.votes);
        renderVoteButtonState(button, item);
        return true;
      }).catch(function (error) {
        setStatus(text(error && error.message) || 'Could not update your vote.', 'error');
        return false;
      }).finally(function () {
        delete state.pending[slug];
        button.disabled = false;
      });
    }

    function duplicateItem(slug, button) {
      var item = findItem(slug);
      if (!item) return Promise.resolve(false);
      if (!customerId) return Promise.resolve(redirectToLogin());
      if (state.pending[slug]) return Promise.resolve(false);
      state.pending[slug] = true;
      button.disabled = true;
      setStatus('Adding this design to your account…', '');
      return requestJson(proxyRoot + '/gallery/duplicate', {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json'
        },
        body: JSON.stringify({ slug: slug })
      }).then(function () {
        setStatus('Design added. Taking you to My Designs…', 'success');
        window.location.assign(new URL(myDesignsUrl, window.location.origin).toString());
        return true;
      }).catch(function (error) {
        setStatus(text(error && error.message) || 'Could not add this design to your account.', 'error');
        button.disabled = false;
        return false;
      }).finally(function () {
        delete state.pending[slug];
      });
    }

    function loadGallery(cursor) {
      state.loading = true;
      // Only paint the loading placeholder on the first load — a Load more
      // fetch keeps the existing cards in place (the disabled button is the
      // progress signal) instead of rebuilding the whole grid twice.
      if (!state.items.length) renderItems();
      renderLoadMore();
      setStatus('', '');
      var requestUrl = new URL(proxyRoot + '/gallery', window.location.origin);
      if (text(cursor)) requestUrl.searchParams.set('cursor', text(cursor));
      return requestJson(requestUrl.toString()).then(function (payload) {
        var gallery = payload && payload.gallery && typeof payload.gallery === 'object' ? payload.gallery : {};
        var newItems = (Array.isArray(gallery.items) ? gallery.items : [])
          .map(normalizeItem)
          .filter(function (entry) { return entry.slug; });
        state.items = text(cursor) ? state.items.concat(newItems) : newItems;
        state.nextCursor = text(gallery.nextCursor);
        state.hasNextPage = gallery.hasNextPage === true && !!state.nextCursor;
      }).catch(function (error) {
        if (!text(cursor)) {
          state.items = [];
          state.hasNextPage = false;
          state.nextCursor = '';
        }
        setStatus(text(error && error.message) || 'Could not load shared designs. Please try again.', 'error');
      }).finally(function () {
        state.loading = false;
        renderItems();
        renderLoadMore();
      });
    }

    if (listNode) {
      listNode.addEventListener('click', function (event) {
        var target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        var voteButton = target.closest('[data-bg-gallery-vote]');
        if (voteButton) {
          void voteItem(text(voteButton.getAttribute('data-bg-gallery-vote')), voteButton);
          return;
        }
        var duplicateButton = target.closest('[data-bg-gallery-duplicate]');
        if (duplicateButton) {
          void duplicateItem(text(duplicateButton.getAttribute('data-bg-gallery-duplicate')), duplicateButton);
        }
      });
    }

    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', function () {
        if (state.loading || !state.hasNextPage) return;
        void loadGallery(state.nextCursor);
      });
    }

    void loadGallery('');

    return {
      refresh: function () {
        return loadGallery('');
      }
    };
  }

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('[data-bg-design-gallery-root]');
    if (!root) return;
    var controller = initRoot(root);
    window.BGDesignGallery = controller || {
      refresh: function () {
        return Promise.resolve(false);
      }
    };
  });
})();
